import { useUserContent } from "@coaching-culture/api-client";
import { ContentType, UserContentDto } from "@coaching-culture/types";
import { useUser } from "auth";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import { sortBy } from "lodash";
import {
  Box,
  Button,
  EmptyState,
  Flex,
  Skeleton,
  Text,
} from "@coaching-culture/ui";
import { FaBookOpen } from "react-icons/fa";
import { clientUrlForContent } from "utils/content";
import { trackHome } from "./track";
import {
  CardBody,
  CategoryLabel,
  HomeCard,
  LoadError,
  Muted,
  RuleList,
} from "./styles";

/** How many in-progress items the card lists. */
const ITEMS_SHOWN = 3;

const typeLabels: Record<ContentType, string> = {
  module: "Mindset",
  lesson: "Lesson",
  assessment: "Assessment",
  learncast: "LearnCast",
};

const lastStarted = (c: UserContentDto) =>
  Math.max(
    0,
    ...c.attempts.filter((a) => a.completedOn == null).map((a) => a.startedOn),
  );

export type ContinueLearningCardProps = {
  /** Content types whose product is enabled for this org. */
  enabledTypes: ContentType[];
};

export function ContinueLearningCard({
  enabledTypes,
}: ContinueLearningCardProps) {
  const [user] = useUser();
  const content = useUserContent(user.id);

  const inProgress = sortBy(
    (content.data ?? []).filter(
      (c) =>
        enabledTypes.includes(c.type) &&
        c.attempts.some((a) => a.completedOn == null),
    ),
    (c) => -lastStarted(c),
  );

  return (
    <HomeCard
      title="Pick up where you left off"
      titleId="home-learning-title"
      busy={content.isLoading}
    >
      {content.isLoading ? (
        <CardBody>
          <Skeleton width="80%" mb={2} />
          <Skeleton width="60%" />
        </CardBody>
      ) : content.isError ? (
        <LoadError
          message="We couldn't load your learning."
          onRetry={() => content.refetch()}
        />
      ) : inProgress.length === 0 ? (
        <EmptyState
          compact
          icon={FaBookOpen}
          action={
            <Button small filled={false} to="/dashboard/content/me">
              Browse your content
            </Button>
          }
        >
          Nothing in progress.
        </EmptyState>
      ) : (
        <CardBody>
          <RuleList>
            {inProgress.slice(0, ITEMS_SHOWN).map((c) => (
              <Flex
                as="li"
                key={`${c.type}:${c.id}`}
                alignItems="center"
                py={2}
                style={{ gap: 12 }}
              >
                <CategoryLabel category="learning">
                  {typeLabels[c.type]}
                </CategoryLabel>
                <Box flex={1} minWidth={0}>
                  <Text
                    m={0}
                    fontWeight={600}
                    style={{ overflowWrap: "anywhere" }}
                  >
                    {c.name}
                  </Text>
                  <Muted m={0} fontSize="caption">
                    Started{" "}
                    {formatDistanceToNowStrict(fromUnixTime(lastStarted(c)), {
                      addSuffix: true,
                    })}
                  </Muted>
                </Box>
                <Button
                  small
                  filled={false}
                  to={clientUrlForContent(c)}
                  aria-label={`Resume ${c.name}`}
                  onClick={() =>
                    trackHome("home_resume_learning", { type: c.type })
                  }
                >
                  Resume
                </Button>
              </Flex>
            ))}
          </RuleList>
        </CardBody>
      )}
    </HomeCard>
  );
}
